import {
  getCSRFToken,
  dispatchToast,
  processResponseTriggers,
  liveSettleTarget,
} from "../shared/htmx-helpers";
import { buildVariantParams } from "./variant-params";
import { editorAutosave } from "./editor-autosave";
import { editorPreview } from "./editor-preview";
import { carryBookingPreviewProperty } from "./booking-preview-url";
import { editorAiReview } from "./editor-ai-review";
import { editorHistory } from "./editor-history";

// ContrastMessages is the shape of the data-msg-contrast JSON blob
// (cms_editor.templ cmsContrastMessages) — the localized strings the Design
// contrast card renders. `hint` is keyed by "illegible" | "large_ok" |
// "faint"; `pair` by ContrastPairKey. Both are read defensively (missing keys
// fall back to a readable default) so a future pair/hint never hard-crashes.
export interface ContrastMessages {
  heading: string;
  apply: string;
  suggestion: string;
  // chipTooltip is the tap-reveal bubble text for the section-title contrast
  // pill (CHANGE 3) — shown when the operator taps the "⚠ N" pill.
  chipTooltip: string;
  hint: Record<string, string>;
  pair: Record<string, string>;
}

export interface CMSEditorConfig {
  baseURL: string;
  locale: string;
  slug: string;
  hasChanges: boolean;
  section: string;
  // context is the CMS editor's page-type ("homepage", "property", "about",
  // "site", …) — CMSEditorData.Context server-side. Constant for the life of
  // this component instance (a target-switcher change body swaps to a whole
  // new page, re-running init() with a fresh config). Used, alongside
  // `section` and `slug`, to stamp the on-screen target (Task 6.4).
  context: string;
  // previewURL is the stable base draft-preview URL (render-preview?...&draft=1,
  // never carrying publication=). Seeded into $store.cmsPreviewURL in init(); the
  // version-preview iframe :src derives its URL from that store + '&publication='.
  previewURL: string;
  // previewUpdate seeds _currentPreviewUpdate for the entry section's update mode
  // ("in-place" | "reload" | "theme-live"). Updated on each rail nav from the
  // clicked link's data-cms-preview-update attr so it's never stale after nav.
  previewUpdate?: string;
  // paused: the website is temporarily offline. Disables the publish button
  // (autosave/drafts still work) — server-derived, constant for the page's
  // lifetime (no client-side toggle exists for it).
  paused: boolean;
  // previewMode seeds the reactive `previewMode` component var ("full" |
  // "section", cms_editor.templ's d.Preview literal — see cmsEditorXData's
  // doc comment for the two-namespaces-for-one-concept note) that drives the
  // preview-mode switcher's active state and setPreviewMode()'s no-op guard.
  previewMode: string;
  // serverPending / publishInFlight seed the publish union from the server's
  // last shown state (PublishStateService.Peek) and the latest build run, so
  // a site with only server-side changes doesn't flash a disabled Publish
  // before the async /api/publish-state fetch lands. Optional (default false).
  serverPending?: boolean;
  publishInFlight?: boolean;
  // pageTargets is the allowlist of generic site:<dotted.block> autosave targets
  // for registered site-level CMS pages (service.CMSPageAutosaveTargets, injected
  // by cmsEditorXData). _targetRoute routes these generically, so adding a page
  // needs no TS change. Optional: absent → no generic page routes.
  pageTargets?: string[];
}

// CmsEditor is the fully-merged shape of the Alpine component that cmsEditor()
// returns — the union of the core wiring here plus every mixin spread into it
// (editorAutosave / editorPreview / editorAiReview / editorHistory). It is the
// SINGLE `this` type shared across all mixins: because Alpine binds `this` to
// the merged object at runtime, each mixin's methods reference sibling concerns
// (`this._flush()`, `this._refreshPreview()`, …) and type-check against this
// interface via `ThisType<CmsEditor>` on their return type. Alpine magics
// ($el/$root/$store/$watch) are reached with `(this as any)` as in the
// original, so they are intentionally absent here.
export interface CmsEditor {
  // ── core state ──
  hasChanges: boolean;
  // The publish union from GET {base}/api/publish-state (see core below).
  serverPending: boolean;
  // A publish build is queued/running (GET /api/publish-state in_flight).
  publishInFlight: boolean;
  paused: boolean;
  section: string;
  // previewMode ("full" | "section") drives the preview-mode switcher's
  // reactive active state (T1 mobile fix #5). See CMSEditorConfig.previewMode.
  previewMode: string;
  // Monotonic token bumped on every setPreviewMode() call (fix-loop round 2).
  // Lets an in-flight call detect it's been superseded by a later one — a
  // rapid Full→Section→Full click sequence fires overlapping htmx.ajax
  // requests at the same #cms-editor-leftpane target with no cancellation;
  // without this, whichever settles LAST wins the DOM/store/URL regardless
  // of which button the operator is actually looking at.
  _previewModeSeq: number;
  // previewLoading drives the preview-pane loading overlay: true while the
  // iframe is (re)navigating to a full page/section render (the heavy Astro
  // sidecar wait on initial load + CMS page switch + section-mode section
  // switch), false once the iframe fires `load`. A full-page-mode section
  // switch only scrolls (no iframe reload), so it never sets this.
  previewLoading: boolean;
  // Safety timer that force-clears previewLoading if the iframe never fires
  // `load` (error / blocked / same-URL no-op nav) — so the overlay can't stick.
  _previewLoadTimer: ReturnType<typeof setTimeout> | null;
  // showConsentBannerPreview (T4) is the Design-only, EPHEMERAL show/hide state
  // for the inert consent-banner sample (cms_editor.templ's
  // data-cms-consent-preview-toggle). It is pure client UI state — never
  // autosaved, never persisted, never read from/written to localStorage or
  // sessionStorage. Defaults false; reset to false on every leftpane
  // (re)render (_resetConsentPreviewOnNav — entering Design starts hidden,
  // leaving Design clears it so a later return doesn't resurrect a stale show).
  showConsentBannerPreview: boolean;
  _msgs: Record<string, string>;
  // Localized strings for the Design contrast card (a11y-03), parsed once in
  // init() from the data-msg-contrast JSON blob. Keys mirror the client's
  // ContrastPairKey / hint-key values so rendering is a direct lookup.
  _contrastMsgs: ContrastMessages;
  // ── autosave state ──
  _saveTimer: ReturnType<typeof setTimeout> | null;
  _capTimer: ReturnType<typeof setTimeout> | null;
  _pending: Record<string, Record<string, unknown>>;
  _pendingCaptions: Record<string, Record<string, unknown>>;
  _flushing: boolean;
  _flushRun: Promise<boolean> | null;
  // Allowed {placeholder} names per "<target>\n<field>" for placeholder-
  // bearing fields (data-cms-placeholders) — see content-placeholders.ts.
  _placeholderRules: Record<string, string[]>;
  _placeholderSections: Record<string, string>;
  _placeholderFocusPending: boolean;
  _placeholderBaselines: Record<string, string>;
  // ── preview state ──
  _currentPreviewUpdate: string;
  _previewTimer: ReturnType<typeof setTimeout> | null;
  _recolorTimer: ReturnType<typeof setTimeout> | null;
  _darkReloadTimer: ReturnType<typeof setTimeout> | null;
  _previewDirty: boolean;
  _previewScroll: { el: Element; top: number; left: number } | null;
  _suppressScrollCapture: boolean;
  // ── ai-review state ──
  _aiEditsRefreshTimer: ReturnType<typeof setTimeout> | null;
  _aiEditsRefreshing: boolean;
  _aiEditsRefreshQueued: boolean;
  // ── history state ──
  _targetStampTimer: ReturnType<typeof setTimeout> | null;

  // ── core methods ──
  init(): void;
  destroy(): void;
  _refreshPublishState(): Promise<void>;
  _syncInFlightPoll(): void;
  canPublish(): boolean;
  _onBuildCompleted(): void;
  _onRenderCompleted(): void;
  _setDirty(v: boolean): void;
  _main(): HTMLElement | null;
  _contentPath(): string;
  _cssEscape(s: string): string;
  _refreshLeftPane(): void;
  _resetEditState(): void;
  flushAndReviewPublish(): Promise<void>;
  discard(): Promise<void>;
  onVersionRestored(): void;
  _onVariantChange(select: HTMLSelectElement): Promise<void>;
  setPreviewMode(mode: "full" | "section"): Promise<void>;

  // ── autosave methods ──
  _onFieldEvent(e: Event): void;
  _queueEdit(target: string | undefined, field: string, value: unknown, placeholders?: string[]): void;
  _targetRoute(
    target: string,
    fields: Record<string, unknown>,
  ): { path: string; overlay: Record<string, unknown> } | null;
  _onCaptionEvent(e: Event): void;
  _flushCaptions(): Promise<boolean>;
  flushPending(): Promise<boolean>;
  _flush(): Promise<boolean>;
  _flushOnce(): Promise<boolean>;
  _hasPending(): boolean;
  _invalidPlaceholderFields(target: string, fields: Record<string, unknown>): string[];
  _invalidPlaceholderElements(): Array<[HTMLInputElement, string, string, string]>;
  _hasInvalidPlaceholders(): boolean;
  _focusFirstInvalidPlaceholder(): void;
  _blockOnInvalidPlaceholders(): boolean;
  _editorURL(): string;
  _restoreInvalidPlaceholderFields(): void;
  _onPlaceholderChipClick(btn: HTMLElement): void;

  // ── preview methods ──
  _iframe(): HTMLIFrameElement | null;
  _previewMode(): string;
  _isBookingFullPage(): boolean;
  _previewUpdate(): string;
  _refreshPreviewReload(): void;
  _updatePreviewInPlace(): Promise<void>;
  _patchPreviewTheme(field: string, value: string): void;
  _applyPreviewTheme(theme: "light" | "dark"): void;
  setPreviewTheme(theme: "light" | "dark"): void;
  _onPreviewLoad(): void;
  // Arms the preview-pane loading overlay (sets previewLoading true + a safety
  // timeout). Called wherever the iframe is about to (re)navigate. See its impl.
  _beginPreviewLoad(): void;
  // Faithful booking preview switcher (?tab= / ?property= on the preview URL)
  // + "refresh prices". See editor-preview.ts.
  bookingPreviewParam(name: string, fallback: string): string;
  bookingPreviewPropertyValue(ids: readonly string[]): string;
  setBookingPreviewParam(name: string, value: string): void;
  refreshBookingPreview(url: string, errorMsg?: string): Promise<void>;
  // True while the "refresh prices" POST is in flight (disables the button).
  bookingPreviewRefreshing: boolean;
  // Seeds $store.cmsPreviewURL on init(); keeps booking tab/property across
  // a same-page re-init. See editor-preview.ts.
  _seedPreviewURLStore(url: string): void;
  _restorePreviewScroll(): void;
  _injectPreviewFont(doc: Document, family: string): void;
  _updateContrastWarnings(): void;
  // Applies a suggested accessible color to a ColorField (primary_color /
  // accent_color) exactly as an operator edit would — updating the swatch,
  // firing autosave + live theme-patch + a re-check. See its implementation.
  _applyContrastSuggestion(field: string, value: string): void;
  // Sets one neutral ColorField (background/surface/text/text_muted) exactly as
  // an operator edit would — the shared low-level set-path the palette-mood
  // apply drives all four fields through.
  _setNeutralColor(field: string, value: string): void;
  // Applies a palette-mood's four neutral hexes (cms-09) through the ColorField
  // set-path; brand colors are untouched. See its implementation.
  _applyPaletteMood(bg: string, surface: string, text: string, muted: string): void;
  // Re-highlights the mood the current neutrals match (aria-pressed); clears it
  // when none matches. Called on load + after every site:design edit.
  _updateActiveMood(): void;
  _scrollPreviewToSection(id: string): void;
  // Sets the active section AND, in full-page preview mode, scrolls the preview
  // to it immediately on click — decoupling instant feedback from the (now fast,
  // but never zero) leftpane GET. See its implementation.
  _switchSection(id: string): void;
  onExternalDraftSaved(): void;
  _schedulePreview(): void;
  _previewVisible(): boolean;
  _refreshPreview(): void;

  // ── consent-banner sample preview (T4) ──
  // The iframe wrapper the render-preview handler emits ONLY for Design's own
  // &consent_sample=1 URL (handler_render_preview.go) — null off Design, and
  // null when the sample failed to render (the error node below is present
  // instead) or the sidecar/page render failed outright (neither node exists).
  _consentPreviewWrap(): HTMLElement | null;
  // The inert error placeholder the handler emits INSTEAD of the wrapper when
  // the sidecar's consent-banner render call failed (but the rest of the page
  // rendered fine) — carries the already-localized message in data-message.
  _consentPreviewErrorNode(): HTMLElement | null;
  _consentPreviewToggleBtn(): HTMLButtonElement | null;
  _consentPreviewErrorBanner(): HTMLElement | null;
  // Reads the LIVE draft value straight off the Design form's own
  // consent_banner_position select (reflects any unflushed edit); "bottom" is
  // the field's/theme default.
  _currentConsentBannerPosition(): string;
  // Live-patches the sample's bottom/center class + data-position attribute —
  // called by _patchPreviewTheme on every site:design consent_banner_position
  // edit (same theme-live, no-reload path every other Design field uses), and
  // by toggleConsentBannerPreview/_onPreviewLoad so a freshly (re)shown/
  // (re)loaded sample always reflects the current draft position.
  _applyConsentBannerPosition(position: string): void;
  // Reflects showConsentBannerPreview + sample availability onto the toggle
  // button (label/aria-pressed/disabled) and the standalone error banner
  // (outside the iframe) — called whenever the Design leftpane (re)renders or
  // the iframe (re)loads. No-op off Design (no control in the DOM).
  _syncConsentPreviewControl(): void;
  // The show/hide toggle's click handler. ONLY flips the iframe wrapper's
  // `hidden` attribute (+ re-asserts the current position class) — no
  // autosave, no draft write, no localStorage/sessionStorage touch (the
  // sample never runs consent-entry.ts to begin with — see
  // handler_render_preview.go). No-op when the sample is unavailable.
  toggleConsentBannerPreview(): void;
  // Resets showConsentBannerPreview to hidden and re-syncs the (freshly
  // server-rendered) toggle control — called on every leftpane (re)render
  // (rail nav, discard, version restore, variant switch), which covers both
  // "entering Design starts hidden" and "leaving Design resets state".
  _resetConsentPreviewOnNav(): void;

  // ── ai-review methods ──
  _fieldValue(field: string): string;
  _setFieldValue(field: string, val: string, opts?: { suppress?: boolean }): void;
  _onRevertFieldClick(btn: HTMLElement): void;
  _clearAiBadge(field: string): void;
  _clearAiLayerOnEdit(field: string): void;
  _ensureRevertButton(field: string): HTMLElement | null;
  _showAiBadge(field: string): void;
  onCmsFieldFilled(e: CustomEvent): void;
  onCmsListStructureChanged(e: CustomEvent): void;
  _applyListCellEdit(leaf: string, value: string, preValue: string): void;
  _ackAiCell(leafPath: string): void;
  onAutosaveFlushed(): void;
  _scheduleAiEditsRefresh(): void;
  _refreshAiEdits(): void;
  onReviewRevertClick(btn: HTMLElement): Promise<void>;
  onReviewRevertStructuralClick(btn: HTMLElement): Promise<void>;
  onReviewRevertAllClick(): Promise<void>;
  onReviewAcceptClick(btn: HTMLElement): Promise<void>;
  onReviewAcceptAllClick(): Promise<void>;
  _afterAiEditsAccepted(): void;
  _afterAssistantRevert(poiOverrideOnly?: boolean): void;
  _afterOperatorRowRemoved(): void;
  _syncRevertButton(field: string, value: string): void;

  // ── history methods ──
  _bindHistory(): void;
  _assistantConfigured(): boolean;
  _scheduleTargetStamp(): void;
  _stampTarget(): Promise<void>;
}

// In-CMS back/forward state (module scope so the single window popstate listener
// is bound once and always reaches the LIVE editor instance — the body swap on
// a page-switch / popstate re-creates the Alpine component, which re-points
// _activeEditor at itself in init()).
let _activeEditor: CmsEditor | null = null;
let _historyBound = false;
let _previewParentScrollBound = false;
let _restoreBound = false;
let _externalDraftSavedBound = false;
let _fieldFilledBound = false;
let _listStructureBound = false;
let _autosaveFlushedBound = false;
let _reviewRevertBound = false;
let _externalDirtyBound = false;
let _beforeSwapBound = false;
let _navIndicatorBound = false;
let _sectionsChangedBound = false;
let _prepublishPublishedBound = false;
// website-publish-state-changed listener (plan 2 D11) — module-bound once,
// reaching the live editor via _activeEditor, like the siblings above.
let _publishStateChangedBound = false;
// In-flight publish poll (final review item 3). While a publish build is
// queued/running the union is re-read every IN_FLIGHT_POLL_MS so the
// "publishing" state clears even when no SSE completion reaches this session
// (another user's publish, a dropped stream). ONE module-level timer — a
// re-init or a second refresh replaces it rather than stacking — owned by the
// editor that scheduled it, so destroy() only cancels its own timer.
const IN_FLIGHT_POLL_MS = 10_000;
let _inFlightPollTimer: ReturnType<typeof setTimeout> | null = null;
let _inFlightPollOwner: unknown = null;
// cms-build-completed listener — module-bound once like the siblings above;
// it used to register per init() and stacked a copy (preview refresh, toast,
// state fetch) on every body-swap re-init.
let _buildCompletedBound = false;
// Bind-once guard for the palette-mood picker's delegated click listener
// (cms-09). Bound at module scope (like the contrast-chip doc listeners) and
// reaching the LIVE instance via _activeEditor, so the many init() re-runs on
// body swap never stack duplicate handlers.
let _moodPickerBound = false;
// Bind-once guard for the consent-banner sample toggle's delegated click
// listener (T4) — mirrors _moodPickerBound immediately above.
let _consentPreviewToggleBound = false;
// Bind-once guard for the htmx:afterSettle listener below (fix-loop round 1).
// It used to register on every init() call (no guard), stacking a duplicate
// listener on each body swap re-init — harmless-by-luck (every stacked copy
// read the SAME live `this` closure at the time it fired, since `this` in
// those copies was always the instance active on the PREVIOUS init(), and
// the fresh init() overwrote the same state each stacked copy would have
// computed) but wasteful (Ndestroy/init
// cycles per nav) and fragile. Now bound ONCE like every other module
// listener; the handler itself was rewritten to reach the LIVE instance via
// _activeEditor (module singleton, reassigned by every init()) instead of a
// closure-captured `this`, so a single binding stays correct across every
// future body swap re-init — mirrors _beforeSwapBound's listener, which
// never captured `this` in the first place.
let _afterSettleBound = false;
// Pre-nav identity snapshot for the body swap cmsMobileTab-reset guard below
// (T1 mobile fix #5, defense-in-depth). htmx:beforeSwap fires with the OLD DOM
// still in place — the last point we can read the pre-nav content-path +
// section before the innerHTML body swap replaces #cms-editor-main with the
// NEW page's element (carrying the new page's dataset), which is why
// htmx:afterSettle below can't read the "old" identity off the DOM — by then
// it's already the new one. Only captured for BODY-target requests;
// left stale (harmless) for every leftpane/form-target request in between.
let _preNavIdentity: string | null = null;
// True once cmsEditor.init() has run in this JS context. A full page load
// resets it (fresh module); a body swap target switch re-runs init() with it
// already true. Used to refresh the hx-preserved panel's review list only on
// re-inits (see init()).
let _editorHasInitialized = false;
// Containers whose spurious-scroll guard is already installed. Keyed by the
// element itself (not a module boolean) so a body swap that swaps in a fresh
// #main-content re-installs the guard instead of silently losing it.
const _scrollGuarded = new WeakSet<HTMLElement>();
// Preview iframes that already have the reveal IntersectionObserver attached.
// Keyed by the iframe element (hx-preserve keeps it alive across body swaps,
// so init() re-runs on the SAME node) — mirrors _scrollGuarded above, so a
// re-init never stacks a second observer on the same persistent iframe.
const _previewRevealObserved = new WeakSet<HTMLElement>();

// Accessors for the module-scoped history singletons above. The history mixin
// (editor-history.ts) lives in a separate module and cannot reassign an
// imported `let` binding directly (ES module imports are read-only), so it
// reaches _activeEditor / _historyBound through these hoisted function
// declarations — keeping the singleton STATE here in editor.ts while the
// _bindHistory logic composes into the mixin. Function declarations (not const
// arrows) so they are hoisted and safe under the editor ↔ editor-history
// circular import (only ever called at runtime, long after module init).
export function getActiveEditor(): CmsEditor | null {
  return _activeEditor;
}
export function isHistoryBound(): boolean {
  return _historyBound;
}
export function markHistoryBound(): void {
  _historyBound = true;
}

// Preview iframe contentWindows that already have the scroll TRACKER attached.
// Keyed by the Window (a full reload swaps the Window → re-track; a fragment
// swap keeps it → listener persists). We track scroll continuously so the last
// VISIBLE position survives the manage-mode display:none toggle (which some
// browsers reset to 0), letting the reveal hook restore it from a stored value
// rather than the — possibly already-reset — live iframe.
// findPreviewScroller returns the element that actually scrolls the preview
// document: the root scrollingElement if IT overflows, otherwise the first
// descendant whose content overflows a scroll/auto overflow-y box (the preview
// renders inside an inner scroll container, so the root often doesn't scroll).
function findPreviewScroller(doc: Document): Element | null {
  const se = doc.scrollingElement;
  if (se && se.scrollHeight > se.clientHeight + 4) return se;
  const view = doc.defaultView;
  const candidates = doc.querySelectorAll<HTMLElement>("body, body *");
  for (const el of candidates) {
    if (el.scrollHeight > el.clientHeight + 4) {
      const oy = view?.getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") return el;
    }
  }
  return se;
}

// htmx:afterSettle fires for every swap in the session (this listener is
// bound once on <body> — see the guard at its registration site below).
// Extracted to a standalone function (not an inline closure inside init())
// so it can be exercised directly in tests without running cmsEditor's full
// Alpine init() lifecycle — mirrors getActiveEditor()/__setActiveEditorForTest
// below, which exist for the same reason.
//
// ?versions=1 deep link (website_detail.templ's "Version history" button).
// open-cms-versions does two jobs: the teleported drawer's Alpine listener
// opens the panel, and it is the content host's htmx trigger
// ("open-cms-versions from:window once") that fetches /cms/versions. htmx only
// binds that trigger when it processes the teleported drawer, and after a
// boosted navigation that happens during htmx's swap settle — later than any
// task init() can schedule. A single early dispatch therefore opened an EMPTY
// drawer and never fetched. So: dispatch now (opens the panel), and dispatch
// once more when htmx has processed the host if it wasn't processed yet. The
// trigger is `once`, so the second dispatch can't double-fetch; a re-open of
// an already-open drawer is a no-op.
export function openVersionsDeepLink(): void {
  const open = () => window.dispatchEvent(new CustomEvent("open-cms-versions"));
  const isHost = (el: unknown) => el instanceof Element && el.matches("[data-cms-versions-host]");
  const host = document.querySelector("[data-cms-versions-host]");
  // htmx stores its per-element state here once the element is processed.
  const processed = !!(host as any)?.["htmx-internal-data"]?.initHash;
  open();
  if (processed) return;
  const onProcessed = (e: Event) => {
    if (!isHost(e.target)) return;
    document.body.removeEventListener("htmx:afterProcessNode", onProcessed);
    open();
  };
  document.body.addEventListener("htmx:afterProcessNode", onProcessed);
}

// Two different "what's the swap target" questions get two different
// answers here — conflating them (fix-loop regression, eb80109e9) broke rail
// nav entirely:
//
//   `live` = liveSettleTarget(e) — null for a target that's been removed
//   from the document by the time afterSettle fires. Needed before calling
//   Alpine.initTree on it (re-initializing a detached tree throws for every
//   expression reading an ancestor scope, STUDIO-BROWSER-16) and before the
//   BODY-swap destroy+init branch below, which also re-inits via Alpine.
//
//   `settled` = the RAW detail.target, detached or not. The CMS rail
//   section switch swaps #cms-editor-leftpane with hx-swap="outerHTML", so
//   on afterSettle detail.target is ALWAYS the detached OLD leftpane —
//   `live` is (correctly) null for it. The rail branch below only reads
//   `settled.id`/`settled.querySelector` (which work fine on a detached
//   node) and the click-time request trigger off detail.requestConfig.elt to
//   drive _activeEditor methods that query the LIVE DOM themselves — it
//   never hands `settled` to Alpine, so gating it on `live` was wrong and
//   silently skipped the whole branch (full-page scroll-to-section,
//   section-mode iframe reload, _currentPreviewUpdate, contrast/mood/
//   consent-preview resync) on every rail click.
export function handleAfterSettle(e: Event): void {
  const live = liveSettleTarget(e);
  const A = (window as any).Alpine;
  if (live && A?.initTree) queueMicrotask(() => A.initTree(live));
  // Full-page navigation (target switcher, popstate back/forward) swaps the
  // whole <body> (plain innerHTML swap — no morph; only hx-preserve elements
  // are kept live). The destroy+init of #cms-editor-main below guarantees a
  // clean re-init of that subtree (historically it re-ran manageMode's local
  // x-data and re-bound every x-show). manageMode now lives on the GLOBAL
  // $store.cmsMobileTab (T1/mobile) instead, so its reactivity is independent of
  // any one subtree's init/destroy — but a store is a SINGLETON, so it no longer
  // gets reset "for free" by the destroy+init below. Without an explicit reset a
  // manage-mode session on one area's Discover would carry the 50/50 split onto
  // the next area page (the R9 regression) — resetManageMode() BEFORE re-init so
  // the freshly-initialized subtree reads the already-cleared value (no stale-
  // split flash). Scoped to a body swap only: rail swaps target
  // #cms-editor-leftpane (a DIV, handled below) and must NOT reset — R9 requires
  // manage-mode to survive in-page section switches. Reset manageMode ONLY
  // (preserve tab): the Edit/Preview tab is a user-chosen viewing preference, not
  // page-scoped state, so a cross-page nav should NOT force the operator back to
  // Edit if they were on Preview (that was the T3 mobile-fixes regression — see
  // resetManageMode() in studio-bundle.ts).
  //
  // T1 mobile fix #5 (defense-in-depth): a BODY-target swap is not, by
  // itself, proof the page actually changed — only that this specific
  // request happened to target body. Compare the pre-nav identity
  // snapshotted in htmx:beforeSwap above against the POST-swap identity
  // (read off the freshly-swapped #cms-editor-main/#cms-editor-preview,
  // both plain server-rendered attributes, not Alpine state) so a
  // hypothetical FUTURE same-page body swap can't reset manageMode the
  // way the preview-mode toggle used to. Every real navigation this
  // branch already handles (target switcher, popstate to a different
  // page OR a different section within the same page) changes at least
  // one of content-path/section, so this preserves today's reset
  // behavior for all of them; a missing snapshot (e.g. first paint) is
  // treated as genuine, matching the previous unconditional reset.
  if (live && live.tagName === "BODY" && A?.destroyTree && A?.initTree) {
    const newContentPath = document.getElementById("cms-editor-main")?.dataset.cmsContentPath;
    const newSection = document.getElementById("cms-editor-preview")?.dataset.cmsSection;
    const newIdentity =
      newContentPath !== undefined && newSection !== undefined
        ? `${newContentPath}|${newSection}`
        : null;
    const genuineNav =
      _preNavIdentity === null || newIdentity === null || _preNavIdentity !== newIdentity;
    queueMicrotask(() => {
      // Reset manageMode ONLY (preserve tab): switching pages should
      // keep whichever tab (Edit/Preview) the operator was on — only
      // the R9 POI-manage takeover is page-scoped and needs clearing.
      if (genuineNav) A.store("cmsMobileTab")?.resetManageMode();
      const main = document.getElementById("cms-editor-main");
      if (main) {
        A.destroyTree(main);
        A.initTree(main);
      }
    });
  }
  // NOTE: the assistant panel is deliberately NOT reconciled by a body swap
  // target switch — it carries hx-preserve (cms_assistant_panel.templ), so
  // htmx's native preserve keeps the live element (its tab, x-show display,
  // conversation, and SSE stream) intact instead of re-rendering it. That is
  // why there is no post-swap tab/display repair here: earlier attempts to
  // reactively re-apply x-show after the swap raced with Alpine's own
  // reconciliation and flipped the tab. hx-preserve takes the panel out of
  // the swap entirely.
  // On rail nav the settled target is #cms-editor-leftpane, ALWAYS detached
  // by the time this fires (outerHTML swap) — read the raw detail.target
  // (`settled`), NOT `live`, which is null for it (see the function comment).
  // Read the clicked link's data attrs to update _currentPreviewUpdate and
  // the persistent iframe. The @click already set this.section before the
  // request fired.
  const settled = (e as CustomEvent).detail?.target as HTMLElement | undefined;
  if (
    settled &&
    (settled.id === "cms-editor-leftpane" ||
      settled.querySelector?.("#cms-editor-form") ||
      settled.querySelector?.("#cms-editor-leftpane"))
  ) {
    // The clicked rail link is detail.requestConfig.elt (the REQUEST trigger).
    // detail.elt on afterSettle is the swap TARGET (#cms-editor-leftpane,
    // a DIV) — using it left `link` null, so the preview-URL store never
    // updated and section-mode nav never reloaded the iframe.
    const d = (e as CustomEvent).detail || {};
    const trigger = (d.requestConfig?.elt || d.elt) as HTMLElement | undefined;
    const link = trigger?.matches?.("a[data-cms-preview-url]")
      ? trigger
      : trigger?.closest?.("a[data-cms-preview-url]");
    if (link) {
      const linkEl = link as HTMLAnchorElement;
      const newPreviewUpdate = linkEl.dataset.cmsPreviewUpdate;
      const newPreviewURL = linkEl.dataset.cmsPreviewUrl;
      // SEO + Images own their preview (the snippet card / the lightbox) and
      // never show the iframe. Loading their render URL into the iframe pulls
      // a non-renderable section=seo placeholder (or a redundant full-page
      // reload) into the frame and strands it there — so for a card surface
      // leave the iframe on its last real content render.
      const isCardSurface = linkEl.dataset.cmsPreviewSurface === "card";
      // Reaches the LIVE instance via _activeEditor, not a closure `this`
      // — this handler is now bound once (see the guard above) and must
      // stay correct across every future body swap re-init.
      if (newPreviewUpdate && _activeEditor) _activeEditor._currentPreviewUpdate = newPreviewUpdate;
      if (!isCardSurface && newPreviewURL && _activeEditor) {
        if (_activeEditor._previewMode() === "full-page" && !_activeEditor._isBookingFullPage()) {
          _activeEditor._scrollPreviewToSection(_activeEditor.section);
        } else {
          // Section mode — and the booking full page, whose rail link URL
          // carries the section's checkout tab (a user-picked tab lasts until
          // this next section click).
          // Drive the persistent iframe via the global store, NOT an
          // imperative f.src: the post-swap initTree re-evaluates :src and
          // would revert an imperative set back to the baked URL. Writing
          // the store reactively reloads the frame to the new section.
          // Section-mode switch reloads the iframe to the new section's
          // render — arm the loading overlay until it paints, but ONLY
          // when the URL actually changes. Re-clicking the already-active
          // section is an identity-equal store write: the iframe :src
          // never re-evaluates, no `load` fires, and an unconditional arm
          // would leave the overlay stuck over a live preview until its
          // 20s safety timeout (mirrors the init() no-op guard).
          const urlStore = (_activeEditor as any).$store?.cmsPreviewURL;
          // Booking preview: keep the operator's chosen accommodation.
          const carried = carryBookingPreviewProperty((urlStore?.value as string) || "", newPreviewURL);
          if (urlStore) {
            if (carried !== urlStore.value) _activeEditor._beginPreviewLoad();
            urlStore.value = carried;
            urlStore.section = _activeEditor.section;
          } else {
            _activeEditor._beginPreviewLoad();
            const f = _activeEditor._iframe();
            if (f) f.src = carried;
          }
        }
      }
    } else if (_activeEditor?._previewMode() === "full-page" && !_activeEditor._isBookingFullPage()) {
      _activeEditor._scrollPreviewToSection(_activeEditor.section);
    }
    // Re-render the contrast card for the freshly-swapped section
    // (a11y-03): entering Design shows it from the current palette;
    // leaving Design self-clears it (no color inputs ⇒ no rows).
    _activeEditor?._updateContrastWarnings();
    // Re-sync the palette-mood highlight for the freshly-swapped section
    // (cms-09): a no-op off Design (no [data-cms-mood] buttons), sets the
    // active mood when the Design section swaps in.
    _activeEditor?._updateActiveMood();
    // Ephemeral consent-banner sample toggle (T4): reset to hidden on
    // EVERY leftpane swap — rail nav (entering Design starts hidden,
    // leaving Design clears stale state) AND discard/version-restore/
    // variant-switch refreshes (which re-render the toggle to its
    // server default anyway). No-op off Design.
    _activeEditor?._resetConsentPreviewOnNav();
    // A held-back placeholder-invalid value is still queued — re-show it (and
    // its error) instead of the last saved value the re-render brought back.
    _activeEditor?._restoreInvalidPlaceholderFields?.();
  }
}

// Test-only: injects the active editor singleton so handleAfterSettle (and
// other module-singleton-dispatched listeners) can be exercised without
// running cmsEditor's full init() lifecycle, which needs a real $el/DOM.
// Mirrors the getActiveEditor() read accessor above; harmless in production
// (dead code outside test imports — nothing calls it there).
export function __setActiveEditorForTest(editor: CmsEditor | null): void {
  _activeEditor = editor;
}

// Puts a Layout select back on the option the server last rendered as selected,
// dropping an unsaved choice. Used when a layout switch cannot be saved.
function restoreServerLayout(select: HTMLSelectElement): void {
  const saved = Array.from(select.options).find((o) => o.defaultSelected);
  if (saved) select.value = saved.value;
}

export function cmsEditor(config: CMSEditorConfig): CmsEditor {
  // Core wiring: the init() event-plumbing + the concern-crossing lifecycle
  // methods (publish/discard/onVersionRestored/_onVariantChange) + the shared
  // low-level helpers every mixin reaches (_setDirty/_main/_contentPath/
  // _cssEscape/_refreshLeftPane/_resetEditState). ThisType<CmsEditor> types
  // `this` here to the full merged object, same as in the mixins.
  const core: Partial<CmsEditor> & ThisType<CmsEditor> = {
    hasChanges: config.hasChanges,
    // serverPending: the publish union from /api/publish-state (DB-derived
    // changes, legal, media, server-side drafts; "unknown" counts as pending —
    // publishing stays possible). Publish enables on hasChanges (client
    // drafts) OR serverPending; Discard stays drafts-only.
    serverPending: !!config.serverPending,
    // publishInFlight: a publish build is queued/running. The union stays
    // pending until the worker commits the derived outputs, so while this
    // holds Publish renders a disabled "publishing" state (no second publish
    // mid-build). Cleared by the re-check on cms-build-completed / the
    // structural signal; edits made during the build still count afterwards.
    publishInFlight: !!config.publishInFlight,

    async _refreshPublishState() {
      try {
        const resp = await fetch(`${config.baseURL}/api/publish-state`, { credentials: "same-origin" });
        if (!resp.ok) return;
        const d = await resp.json();
        this.serverPending = !!d.pending;
        this.publishInFlight = !!d.in_flight;
      } catch {
        /* advisory: keep the last known state */
      } finally {
        this._syncInFlightPoll();
      }
    },

    // _syncInFlightPoll (re)arms the single in-flight poll while
    // publishInFlight holds and cancels it otherwise. A failed request keeps
    // the last state, so a transient error keeps polling while in flight.
    _syncInFlightPoll() {
      if (_inFlightPollTimer) clearTimeout(_inFlightPollTimer);
      _inFlightPollTimer = null;
      _inFlightPollOwner = null;
      if (!this.publishInFlight) return;
      _inFlightPollOwner = this;
      _inFlightPollTimer = setTimeout(() => {
        _inFlightPollTimer = null;
        _inFlightPollOwner = null;
        this._refreshPublishState();
      }, IN_FLIGHT_POLL_MS);
    },

    // destroy: Alpine calls it when the editor root leaves the DOM (body swap
    // away from the CMS). Cancel this editor's in-flight poll; a timer owned
    // by a newer instance is left alone.
    destroy() {
      if (_inFlightPollOwner !== this) return;
      if (_inFlightPollTimer) clearTimeout(_inFlightPollTimer);
      _inFlightPollTimer = null;
      _inFlightPollOwner = null;
    },

    // canPublish drives the header Publish button (cms_editor.templ).
    canPublish() {
      return (this.hasChanges || this.serverPending) && !this.publishInFlight && !this.paused;
    },

    // _onBuildCompleted runs on the SSE chip's cms-build-completed (module-
    // bound listener in init()): refresh the preview, re-check the publish
    // union (clears publishInFlight), toast, and flip the version panel's head
    // badge Publishing… → Live.
    _onBuildCompleted() {
      this._refreshPreview();
      this._refreshPublishState();
      dispatchToast(this._msgs.published, "success");
      document.body.dispatchEvent(new CustomEvent("cms-versions-changed", { bubbles: true }));
    },

    // _onRenderCompleted runs on the chip's cms-render-completed: a render
    // (not a publish) finished — refresh the preview and the publish union,
    // no toast and no version-panel change (renders record no publication).
    _onRenderCompleted() {
      this._refreshPreview();
      this._refreshPublishState();
    },

    // paused: website is offline — publish disabled, caption always shown.
    paused: config.paused,
    // Reactive current section — seeded server-side, re-synced on each rail
    // swap (see the htmx:afterSettle handler) so the header can react to the
    // active tab (e.g. hiding the device toggle on the Images surface).
    section: config.section,
    // Reactive preview mode ("full" | "section") — seeded server-side, kept
    // current by setPreviewMode() below (T1 mobile fix #5). A REAL cross-page
    // body swap (target switcher, popstate) re-runs cmsEditor(config) with a
    // fresh server value, so no explicit reset is needed here.
    previewMode: config.previewMode,
    // See CmsEditor._previewModeSeq's doc comment.
    _previewModeSeq: 0,
    // Starts true: the iframe loads its (baked) src on first paint, and the
    // initial _onPreviewLoad clears it. See CmsEditor.previewLoading.
    previewLoading: true,
    _previewLoadTimer: null,
    bookingPreviewRefreshing: false,
    // See CmsEditor.showConsentBannerPreview's doc comment — always starts
    // hidden; _resetConsentPreviewOnNav re-asserts this on every leftpane swap.
    showConsentBannerPreview: false,
    _msgs: {} as Record<string, string>,
    _contrastMsgs: {
      heading: "",
      apply: "",
      suggestion: "",
      chipTooltip: "",
      hint: {},
      pair: {},
    } as ContrastMessages,

    init() {
      const root = (this as any).$el as HTMLElement;
      this._msgs = {
        saveFailed: root.dataset.msgSaveFailed || "Save failed",
        discarded: root.dataset.msgDiscarded || "Discarded",
        published: root.dataset.msgPublished || "Your changes are live.",
        placeholderBlocked: root.dataset.msgPlaceholderBlocked || "",
      };
      // Deep-link support: website_detail.templ's "Version history" quick-link
      // appends ?versions=1 to open the panel immediately on load. The panel
      // used to be driven by a versionsOpen component var seeded from this same
      // query param; now it's a DrawerTeleported controlled by open/close window
      // events (CMS mobile Task 6), so replicate the one-shot open here instead.
      if (new URLSearchParams(window.location.search).get("versions") === "1") {
        // Deferred: Alpine flushes the root x-data's init() synchronously,
        // BEFORE the descendant <template x-teleport="body"> (the version
        // drawer) is cloned into <body> and registers its
        // @open-cms-versions.window listener. Dispatching synchronously here
        // fires into a DOM with no listener yet — the drawer never opens and
        // the lazy /cms/versions fetch never fires. Yield past the current
        // synchronous flush so the listener exists by the time this runs.
        queueMicrotask(openVersionsDeepLink);
      }
      // Contrast card strings (a11y-03) — one JSON blob, parsed defensively.
      this._contrastMsgs = {
        heading: "",
        apply: "",
        suggestion: "",
        chipTooltip: "",
        hint: {},
        pair: {},
      };
      try {
        const parsed = JSON.parse(root.dataset.msgContrast || "{}") as Partial<ContrastMessages>;
        this._contrastMsgs = {
          heading: parsed.heading || "Check contrast",
          apply: parsed.apply || "Apply",
          suggestion: parsed.suggestion || "Suggestion",
          chipTooltip: parsed.chipTooltip || "Color combinations below the recommended contrast.",
          hint: parsed.hint || {},
          pair: parsed.pair || {},
        };
      } catch {
        /* malformed blob — keep the empty defaults, card still renders labels-less */
      }
      this._msgs.aiBadgeTooltip =
        root.dataset.msgAiBadgeTooltip || "Edited by the assistant — review before publishing";
      this._msgs.revertTooltip = root.dataset.msgRevertTooltip || "Reset to the published value";
      // In-CMS history: register the popstate handler (once) and mark the current
      // entry so back/forward traverse the editor via an upload-safe body swap.
      _activeEditor = this;
      this._bindHistory();
      // Publish enables on the server-side union too (DB-derived changes,
      // legal, media), not only on this editor's drafts — load it async.
      this._refreshPublishState();
      // The preview pane's section-dependent layout + card visibility are driven by
      // a data-cms-section attribute on #cms-editor-preview + CSS (_cms-editor.css),
      // NOT a reactive Alpine :class/x-show. The pane is persistent but re-created by
      // the hx-boost body swap, where a reactive binding could evaluate before the
      // section was seeded and strand the brand favicon beside (not above) the
      // iframe — a race we chased twice. The server renders the initial attribute;
      // this imperative write keeps it current on rail nav (deterministic, no race).
      const setPreviewSection = (v: string) => {
        const pane = document.getElementById("cms-editor-preview");
        if (pane) pane.dataset.cmsSection = v;
      };
      setPreviewSection(this.section);
      // $store.cmsSection is kept in sync as the discoverable global section-state
      // (the header switchers read the component `section` var directly; nothing
      // reactive on the pane reads the store anymore, but it stays available).
      const sectionStore = (this as any).$store?.cmsSection;
      if (sectionStore) sectionStore.value = this.section;
      (this as any).$watch("section", (v: string) => {
        setPreviewSection(v);
        if (sectionStore) sectionStore.value = v;
        this._scheduleTargetStamp();
      });
      // Stamp the on-screen target once on load too (Task 6.4) — covers the
      // initial page render AND every target-switcher change (a body swap
      // navigation to a new URL re-runs init() with fresh config), not just
      // the in-page rail-nav case the $watch above covers.
      this._scheduleTargetStamp();
      // The assistant panel is hx-preserved (cms_assistant_panel.templ), so a
      // target-switch body swap keeps the PREVIOUS page's review list — its
      // group.Current ("which page you're on") is stale for the new target
      // until an edit refreshes it. init() re-runs here with fresh config on
      // every such swap, so on any RE-init (never the first render of this JS
      // context — a full page load resets the flag and the server renders the
      // list correctly) re-fetch #cms-ai-edits for the current target. Rail nav
      // swaps only #cms-editor-leftpane and never re-runs init(), so it is
      // correctly excluded (a section change doesn't move the current group).
      if (_editorHasInitialized) {
        this._scheduleAiEditsRefresh();
      }
      _editorHasInitialized = true;
      // Mirror the live preview URL into a global store for the SAME reason: the
      // persistent iframe's :src is re-evaluated by the post-swap initTree, so it
      // must resolve a store (the current section URL) rather than a baked static
      // URL that reverts. Seeded here from config; updated on each rail nav.
      // On a cross-page body swap, init() re-runs with a new page's previewURL;
      // repointing the persistent iframe at it triggers a heavy full-page render,
      // so arm the loading overlay until `load` fires. A same-URL re-init (no
      // navigation) must NOT arm it (no `load` would follow). First-ever init has
      // an empty store and relies on the default previewLoading=true instead.
      // A same-page booking re-init keeps the switcher's tab/property.
      this._seedPreviewURLStore(config.previewURL);
      // Event delegation — survives the #cms-editor-main outerHTML swap on
      // section switch. Text inputs fire 'input'; MarkdownEditor fires
      // 'markdown-change' on its hidden input; the image widget fires
      // 'cms-image-changed' (added in Task 10) — all bubble + carry the field.
      root.addEventListener("input", (e) => this._onFieldEvent(e));
      // The bool widget is a <select> (a checkbox's value can't carry false).
      // Selects fire both 'input' and 'change', but 'change' is the canonical
      // commit event — listen for it too so the Yes/No value autosaves reliably.
      // The per-section Layout (variant) <select> is routed separately — it's a
      // draft-overlay write + form re-render, NOT a debounced field autosave.
      root.addEventListener("change", (e) => {
        const vt = e.target as HTMLElement | null;
        if (vt && vt.matches && vt.matches("[data-cms-variant-select]")) {
          void this._onVariantChange(vt as HTMLSelectElement);
          return;
        }
        this._onFieldEvent(e);
      });
      root.addEventListener("markdown-change", (e) => this._onFieldEvent(e));
      // Refresh the preview once the SSE chip reports the build completed, and
      // reassure the user with a success toast (the pill's transient "Published ✓"
      // covers the at-a-glance case; the toast confirms it explicitly).
      // Module-bound once, dispatching to the live _activeEditor (see
      // _buildCompletedBound) so body-swap re-inits never stack copies.
      if (!_buildCompletedBound) {
        _buildCompletedBound = true;
        window.addEventListener("cms-build-completed", () => {
          _activeEditor?._onBuildCompleted();
        });
        // A render-mode build (retry, area reconcile, …) completed: not a
        // publish, so no "published" toast — just refresh the preview.
        window.addEventListener("cms-render-completed", () => {
          _activeEditor?._onRenderCompleted();
        });
      }
      // The shared pre-publish diff drawer's confirm button POSTs /api/publish
      // itself (hx-post) and fires this event on success (see
      // prepublish_diff.templ). Mirrors the OLD publish()'s success side
      // effects so the build chip + version panel behave identically whether
      // publish happened via this drawer or the management surface's copy.
      // Bound ONCE at module scope, dispatching to the live _activeEditor, for
      // the same re-init-safety reason as cms-version-restored below — a
      // body swap re-runs init() and would otherwise stack duplicate
      // listeners (each firing on its own stale editor instance).
      if (!_prepublishPublishedBound) {
        _prepublishPublishedBound = true;
        window.addEventListener("prepublish-published", () => {
          _activeEditor?._setDirty(false);
          // Optimistic while the publish run is in flight (the server reports
          // in_flight from the publish run created by this POST);
          // cms-build-completed re-checks the union.
          if (_activeEditor) {
            _activeEditor.serverPending = false;
            _activeEditor.publishInFlight = true;
            // No SSE may reach this session — poll until in_flight clears.
            _activeEditor._syncInFlightPoll();
          }
          // Drive the SSE build-status chip (optimistic "publishing") + refresh the
          // version-history panel (the new publication row is recorded synchronously).
          window.dispatchEvent(new CustomEvent("cms-build-started"));
          document.body.dispatchEvent(new CustomEvent("cms-versions-changed", { bubbles: true }));
        });
      }
      // Structural refresh (plan 2 D11): every successful website/CMS
      // mutation carries website-publish-state-changed (server middleware via
      // HX-Trigger, or signalPublishStateChanged() after a raw fetch()). The
      // event carries no state — re-read the union, debounced 750ms like the
      // management button and build chip.
      if (!_publishStateChangedBound) {
        _publishStateChangedBound = true;
        let t: ReturnType<typeof setTimeout> | null = null;
        document.body.addEventListener("website-publish-state-changed", () => {
          if (t) clearTimeout(t);
          t = setTimeout(() => _activeEditor?._refreshPublishState(), 750);
        });
      }
      // Restore reaches the live editor via a window event (the HTMX-swapped
      // panel can't call a component method under the post-swap initTree). Bind
      // ONCE at module scope, dispatching to the live _activeEditor, so a
      // body swap (which re-runs init) never stacks duplicate listeners —
      // mirrors the _historyBound popstate pattern below.
      if (!_restoreBound) {
        _restoreBound = true;
        window.addEventListener("cms-version-restored", () => {
          _activeEditor?.onVersionRestored();
        });
      }
      // Seasonal envelope images write through the set-seasonal-image draft
      // route (area-envelope-images.ts) — a raw fetch OUTSIDE this editor's
      // autosave flush, so its content change never hits the flush-driven
      // preview reload (flushPending -> _refreshPreviewReload). That component
      // re-dispatches the endpoint's website-content-draft-saved HX-Trigger on
      // the body; reload the preview here so the new image shows without a
      // manual refresh. Module-bound once (like cms-version-restored above) so
      // body swaps never stack duplicate listeners.
      if (!_externalDraftSavedBound) {
        _externalDraftSavedBound = true;
        document.body.addEventListener("website-content-draft-saved", () => {
          _activeEditor?.onExternalDraftSaved();
        });
      }
      // Adding/removing an optional section (the "+ Add section" picker or the
      // rail remove control) changes the section STRUCTURE, not a field value,
      // so the field-autosave flush -> preview reload never fires. The endpoint
      // returns 204 + the cms-sections-changed HX-Trigger; the rail self-refreshes
      // off it (hx-select) but the preview iframe would stay stale (added section
      // missing / removed section still shown) until a manual reload. Reload the
      // preview here so the structural change shows immediately. Bound ONCE at
      // module scope like the siblings above so body swaps never stack listeners.
      if (!_sectionsChangedBound) {
        _sectionsChangedBound = true;
        document.body.addEventListener("cms-sections-changed", () => {
          _activeEditor?._refreshPreviewReload();
        });
      }
      // The assistant's live-apply SSE event (layout.templ forwards the raw
      // htmx:sseMessage to a window CustomEvent whose detail.data is the JSON
      // payload string). Bound ONCE at module scope for the same reason as
      // cms-version-restored above — a body swap re-runs init() and would
      // otherwise stack duplicate listeners on the surviving window.
      if (!_fieldFilledBound) {
        _fieldFilledBound = true;
        window.addEventListener("cms_field_filled", (e) => {
          _activeEditor?.onCmsFieldFilled(e as CustomEvent);
        });
      }
      // The assistant's list-structure SSE event (Task 8; layout.templ forwards
      // the raw htmx:sseMessage to a window CustomEvent whose detail.data is the
      // JSON payload string — same forwarding mechanism as cms_field_filled
      // above). Bound ONCE at module scope for the same re-init-safety reason as
      // cms_field_filled/cms-version-restored above.
      if (!_listStructureBound) {
        _listStructureBound = true;
        window.addEventListener("cms_list_structure_changed", (e) => {
          _activeEditor?.onCmsListStructureChanged(e as CustomEvent);
        });
      }
      // Bridge for surfaces rendered INSIDE the editor that write through their
      // own raw-fetch save path rather than the field-autosave flush — today
      // the CMS picker's photo-credit takeover (image-picker.ts saveCredit,
      // Task 9). A confirmed credit save dispatches this on window (see
      // saveImageCredit in image-credits.ts); route it to the SAME dirty
      // chokepoint content-field edits use so the editor's own Publish button
      // reacts. Bound ONCE at module scope for the same re-init-safety reason
      // as cms_list_structure_changed/cms_field_filled above.
      if (!_externalDirtyBound) {
        _externalDirtyBound = true;
        window.addEventListener("cms-external-dirty", () => {
          _activeEditor?._setDirty(true);
        });
      }
      // Live-refresh the "AI edits (N)" review list (#cms-ai-edits, Task 5.3a)
      // after every draft save, including a review-list revert (Task 5.4).
      // cms-autosave-flushed is dispatched on document.body with
      // bubbles:true (_flush below, and _afterAssistantRevert), which
      // propagates up through document to window — so one module-scoped
      // listener here catches every save/revert regardless of which
      // component instance triggered it, same re-init-safety rationale as
      // cms-version-restored/cms_field_filled above.
      if (!_autosaveFlushedBound) {
        _autosaveFlushedBound = true;
        window.addEventListener("cms-autosave-flushed", () => {
          _activeEditor?.onAutosaveFlushed();
        });
      }
      // Review-list ("AI edits (N)") revert + accept buttons (Task 5.3b's
      // revert endpoints, Task 6's accept endpoints — wired here). Delegated
      // on document.body (module-bound once) rather than `root` — the review
      // list is MOUNTED by Task 6.2's assistant panel, whose position
      // relative to this component's root isn't this task's concern. Matches
      // ONLY [data-cms-revert-key] / [data-cms-revert-structural] /
      // [data-cms-revert-all] / [data-cms-accept-path] / [data-cms-accept-all]
      // — DISTINCT attributes from the on-screen widget's
      // [data-cms-revert-field] (delegated on `root` below) — so the
      // delegated handlers never collide. [data-cms-revert-structural]
      // (Task 9) marks a structural add/remove row's revert button — see
      // cmsAIEditRevertAttrs (cms_ai_edits_review.templ).
      if (!_reviewRevertBound) {
        _reviewRevertBound = true;
        document.body.addEventListener("click", (e) => {
          const target = e.target as HTMLElement | null;
          if (!target) return;
          const structuralBtn = target.closest("[data-cms-revert-structural]");
          if (structuralBtn) {
            void _activeEditor?.onReviewRevertStructuralClick(structuralBtn as HTMLElement);
            return;
          }
          const keyBtn = target.closest("[data-cms-revert-key]");
          if (keyBtn) {
            void _activeEditor?.onReviewRevertClick(keyBtn as HTMLElement);
            return;
          }
          const allBtn = target.closest("[data-cms-revert-all]");
          if (allBtn) {
            void _activeEditor?.onReviewRevertAllClick();
            return;
          }
          const acceptBtn = target.closest("[data-cms-accept-path]");
          if (acceptBtn) {
            void _activeEditor?.onReviewAcceptClick(acceptBtn as HTMLElement);
            return;
          }
          const acceptAllBtn = target.closest("[data-cms-accept-all]");
          if (acceptAllBtn) {
            void _activeEditor?.onReviewAcceptAllClick();
          }
        });
      }
      // #main-content (the app shell's scroll area) must never scroll while the
      // CMS editor is mounted: the editor is a viewport-height shell whose panes
      // (rail, left form, preview, assistant) each scroll internally. A left-pane
      // HTMX swap transiently overflows #main-content before the flex chain
      // re-settles — on the "Änderungen" tab the tall AI-edits review list briefly
      // escapes its overflow-y-auto clamp — and the browser scrolls the swapped
      // content into view. That scroll lands a beat after htmx:afterSettle and
      // then STICKS (nothing resets it), shoving the editor header off the top with
      // an unreachable blank strip below (reported only on the changes tab — the
      // only pane tall enough to trigger the transient overflow). Clamp it back to
      // the top on any scroll while the editor shell is present.
      const mainContent = document.getElementById("main-content");
      if (mainContent && !_scrollGuarded.has(mainContent)) {
        _scrollGuarded.add(mainContent);
        mainContent.addEventListener(
          "scroll",
          () => {
            if (mainContent.scrollTop !== 0 && document.getElementById("cms-editor-main")) {
              mainContent.scrollTop = 0;
            }
          },
          { passive: true },
        );
      }
      // Snapshot the pre-nav identity (content-path + section) for the
      // body swap reset guard in the htmx:afterSettle handler below (T1
      // mobile fix #5). Must run in htmx:beforeSwap — the last point the OLD
      // #cms-editor-main/#cms-editor-preview are still in the DOM before the
      // body swap replaces them. Bound ONCE at module scope (same
      // re-init-safety reason as every other `if (!_xBound)` listener above).
      if (!_beforeSwapBound) {
        _beforeSwapBound = true;
        document.body.addEventListener("htmx:beforeSwap", (e) => {
          const tgt = (e as CustomEvent).detail?.target as HTMLElement | undefined;
          if (tgt && tgt.tagName === "BODY" && _activeEditor) {
            _preNavIdentity = `${_activeEditor._contentPath()}|${_activeEditor.section}`;
          }
        });
      }
      // CMS PAGE-SWITCH loading overlay. A page switch (target switcher, boosted
      // /edit link, popstate) is a full-<body> innerHTML swap that re-runs the whole
      // editor handler for the new page AND fires a heavy full-page preview
      // render — multi-second, and until now a blind pause on the OLD page. Show
      // the #cms-nav-overlay (driven by the persistent $store.cmsNav) the instant
      // such a request starts and clear it once it settles/errs. Gated on a
      // body-target request while an editor is mounted, so section-switch
      // (#cms-editor-leftpane), autosave, and non-editor requests never trip it.
      // Bound once at module scope like the sibling body listeners. The store is
      // read via Alpine so the overlay element (which carries hx-preserve) reacts.
      if (!_navIndicatorBound) {
        _navIndicatorBound = true;
        const A = () => (window as any).Alpine;
        const setNav = (v: boolean) =>
          A()?.store?.("cmsNav") && (A().store("cmsNav").navigating = v);
        const isBodyNav = (e: Event) => {
          const d = (e as CustomEvent).detail || {};
          const tgt = (d.target || d.requestConfig?.target) as HTMLElement | undefined;
          return _activeEditor && (tgt === document.body || d.boosted === true);
        };
        document.body.addEventListener("htmx:beforeRequest", (e) => {
          if (isBodyNav(e)) setNav(true);
        });
        // Clear on settle (success) and on any terminal outcome (error, abort,
        // non-2xx) so the overlay can never stick if the new page never renders.
        document.body.addEventListener("htmx:afterSettle", (e) => {
          const tgt = (e as CustomEvent).detail?.target as HTMLElement | undefined;
          if (tgt && tgt.tagName === "BODY") setNav(false);
        });
        document.body.addEventListener("htmx:responseError", () => setNav(false));
        document.body.addEventListener("htmx:sendError", () => setNav(false));
        document.body.addEventListener("htmx:afterRequest", (e) => {
          if (isBodyNav(e)) setNav(false);
        });
      }
      // CONTINGENCY: native HTMX swaps rely on Alpine 3's MutationObserver to
      // init the new subtree's x-data (the image widgets). If a swapped-in
      // picker is inert, this guarantees re-init. Deferred to a microtask so
      // Alpine's observer (queued when htmx mutated the DOM) initializes the
      // swapped subtree first, making this manual pass an idempotent no-op.
      // NOTE: this initTree re-evaluates the swapped subtree's bindings in a
      // scope that does NOT reliably include the cmsEditor component vars — so
      // the preview iframe must bind device sizing to the GLOBAL Alpine store
      // ($store.cmsDevice), not a component var, or it throws "device is not
      // defined" here (deferring changed the timing, not the scope).
      // Bound EXACTLY ONCE (fix-loop round 1) — this used to register on every
      // init() call with no guard, stacking a duplicate listener on each
      // body swap re-init. The handler below reaches the LIVE editor
      // instance via the _activeEditor module singleton (reassigned by every
      // init()), NOT a closure-captured `this`, so a single binding stays
      // correct across every future re-init — mirrors _beforeSwapBound's
      // listener above, which never captured `this` in the first place.
      if (!_afterSettleBound) {
        _afterSettleBound = true;
        document.body.addEventListener("htmx:afterSettle", handleAfterSettle);
      }
      root.addEventListener("cms-image-changed", (e) => {
        const detail = (e as CustomEvent).detail as {
          field: string;
          target?: string;
          value: unknown;
        };
        if (!detail?.field) return;
        this._queueEdit(detail.target, detail.field, detail.value);
      });
      root.addEventListener("cms-caption-changed", (e) => this._onCaptionEvent(e));
      // Repeater cell revert/override: clear that cell's server-side AI-edit row.
      root.addEventListener("cms-ai-cell-ack", (e) => {
        const leafPath = (e as CustomEvent).detail?.leafPath;
        if (typeof leafPath === "string") this._ackAiCell(leafPath);
      });
      // Repeater PERSISTED-row delete (Task 8B): repeater.ts already POSTed the
      // operator-UI row-delete endpoint itself (it holds baseURL/CSRF/the
      // (context,section) pair) — this just reconciles the on-screen form, same
      // as _afterAssistantRevert does after a review-list revert.
      root.addEventListener("cms-row-removed", () => this._afterOperatorRowRemoved());
      root.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement)?.closest?.("[data-cms-revert-field]");
        if (btn) this._onRevertFieldClick(btn as HTMLElement);
        const chip = (e.target as HTMLElement)?.closest?.("[data-cms-placeholder-insert]");
        if (chip) this._onPlaceholderChipClick(chip as HTMLElement);
      });
      // Preview theme: re-stash + re-apply on every iframe (re)load. If the
      // frame is already loaded (no future 'load' for the current document),
      // stash now.
      const previewFrame = this._iframe();
      if (previewFrame) {
        previewFrame.addEventListener("load", () => this._onPreviewLoad());
        if (previewFrame.contentDocument?.readyState === "complete") this._onPreviewLoad();
      }
      // Reveal hook for the _previewDirty skip above: when the preview iframe
      // (hidden during POI manage-mode) becomes visible again, run the ONE
      // refresh that was skipped while hidden — now visible, so
      // _updatePreviewInPlace captures the RETAINED scroll position instead of
      // 0. Bound ONCE per iframe element (WeakSet-guarded, mirrors
      // _scrollGuarded above): the iframe is hx-preserve'd, so a body swap
      // target-switch re-runs init() on the SAME node — a fresh observer per
      // init would stack duplicates. The callback resolves the LIVE editor via
      // module-scoped _activeEditor (same pattern as cms-version-restored
      // etc. above), never a closed-over `this` from a stale init.
      if (previewFrame && !_previewRevealObserved.has(previewFrame)) {
        _previewRevealObserved.add(previewFrame);
        const revealObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || !_activeEditor) continue;
            // Freeze scroll capture until restore completes: the poll must not
            // record the transient post-reveal 0 over the position we're about
            // to put back (see _suppressScrollCapture).
            _activeEditor._suppressScrollCapture = true;
            // Run the refresh that was skipped while hidden (if any). Its
            // in-place swap resets the iframe scroll to 0 — the refresh restores
            // it. If there was NO pending refresh, restore anyway (belt & braces;
            // a no-op when the browser already kept the position).
            if (_activeEditor._previewDirty) {
              _activeEditor._previewDirty = false;
              _activeEditor._refreshPreview();
            } else {
              _activeEditor._restorePreviewScroll();
            }
          }
        });
        revealObserver.observe(previewFrame);
      }
      // Track the preview's scroll by POLLING the actual scrolling element (the
      // iframe's scrollingElement, or an inner overflow container). Event
      // listeners never work here: the scroll is inside the iframe, the document
      // is replaced on reload, and the scroller uses scroll-behavior:smooth.
      // Reading this._iframe().contentDocument fresh each tick is robust to all
      // of that. CRITICAL: only STORE while the scroller is actually scrollable
      // (content taller than the frame) — while hidden/just-revealed the iframe
      // content collapses to the frame height with scrollTop 0, and storing that
      // would clobber the real pre-hide position we need to restore.
      if (!_previewParentScrollBound) {
        _previewParentScrollBound = true;
        let _scrollerCache: Element | null = null;
        setInterval(() => {
          const ed = _activeEditor as any;
          if (!ed || !ed._previewVisible?.()) return;
          if (ed._suppressScrollCapture) return; // reveal→restore in flight
          const doc = ed._iframe?.()?.contentDocument as Document | undefined;
          if (!doc) return;
          if (
            !_scrollerCache ||
            !_scrollerCache.isConnected ||
            _scrollerCache.ownerDocument !== doc ||
            _scrollerCache.scrollHeight <= _scrollerCache.clientHeight + 4
          ) {
            _scrollerCache = findPreviewScroller(doc);
          }
          const el = _scrollerCache;
          if (!el || el.scrollHeight <= el.clientHeight + 4) return; // not scrollable → don't clobber
          ed._previewScroll = { el, top: el.scrollTop, left: el.scrollLeft };
        }, 250);
      }
      // Palette-mood picker (cms-09): a delegated click listener catching any
      // [data-cms-mood] button. Bound ONCE at module scope (mirrors the
      // contrast-chip doc listeners) and reaching the LIVE editor via
      // _activeEditor, so it stays correct across every body swap re-init
      // without stacking duplicates. Reads the four mood hexes off the button's
      // data-mood-* attrs, applies them through the neutral ColorField set-path
      // (_applyPaletteMood → one coalesced site:design PUT), then re-highlights
      // the now-active mood.
      if (!_moodPickerBound) {
        _moodPickerBound = true;
        document.addEventListener("click", (e) => {
          const btn = (e.target as HTMLElement | null)?.closest?.("[data-cms-mood]") as
            HTMLElement | null | undefined;
          if (!btn || !_activeEditor) return;
          _activeEditor._applyPaletteMood(
            btn.dataset.moodBg || "",
            btn.dataset.moodSurface || "",
            btn.dataset.moodText || "",
            btn.dataset.moodMuted || "",
          );
          _activeEditor._updateActiveMood();
        });
      }
      // Consent-banner sample toggle (T4): a delegated click listener catching
      // [data-cms-consent-preview-toggle]. Bound ONCE at module scope (mirrors
      // the mood-picker listener immediately above) and reaching the LIVE
      // editor via _activeEditor. The button itself carries no Alpine
      // attributes (server-rendered inert, cms_editor.templ) — same vanilla
      // delegation style as the mood picker / contrast chip.
      if (!_consentPreviewToggleBound) {
        _consentPreviewToggleBound = true;
        document.addEventListener("click", (e) => {
          const btn = (e.target as HTMLElement | null)?.closest?.(
            "[data-cms-consent-preview-toggle]",
          ) as HTMLButtonElement | null | undefined;
          if (!btn || btn.disabled || !_activeEditor) return;
          _activeEditor.toggleConsentBannerPreview();
        });
      }
      // Render the contrast card from the CURRENT palette on load (a11y-03) —
      // not only after the first edit — so opening the Design section (or a
      // deep link straight to it) shows the state immediately. Deferred past
      // the synchronous Alpine flush so the server-rendered color inputs are
      // reachable. Self-clears on non-Design sections (no color inputs ⇒ no
      // rows ⇒ box emptied + chip hidden). The mood highlight is re-synced on
      // the same tick so a deep-link straight to Design reflects the active mood.
      queueMicrotask(() => {
        this._updateContrastWarnings();
        this._updateActiveMood();
        // Deep-link straight to Design (or a full page load already on it):
        // the toggle just rendered server-side (defaults: hidden, unpressed);
        // sync availability/disabled state once the iframe has had a chance to
        // load (handled by _onPreviewLoad below) — this call covers the
        // pre-load window so a slow sidecar doesn't leave a stale enabled
        // button before the first load event lands.
        this._syncConsentPreviewControl();
      });
    },

    // _setDirty is the single chokepoint for the dirty flag. Beyond flipping
    // this.hasChanges (which drives the Discard/Publish buttons), it broadcasts
    // 'cms-dirty' so the header status pill can fold the unpublished-changes
    // state into its single state machine.
    _setDirty(v: boolean) {
      this.hasChanges = v;
      window.dispatchEvent(new CustomEvent("cms-dirty", { detail: { dirty: v } }));
    },

    _main(): HTMLElement | null {
      // Optional-chained (T4 fix, mirrors editor-preview.ts's _iframe()): a
      // unit test that calls _onPreviewLoad directly (with every OTHER
      // $root-touching method stubbed) has no $root at all —
      // _consentPreviewToggleBtn (called unconditionally from
      // _syncConsentPreviewControl) must not throw there.
      return (
        ((this as any).$root as HTMLElement | undefined)?.querySelector("#cms-editor-main") ??
        null
      );
    },
    _contentPath(): string {
      return (
        this._main()?.dataset.cmsContentPath ||
        `content/properties/${config.locale}/${config.slug}.json`
      );
    },

    // _cssEscape escapes a string for safe use in a CSS attribute selector,
    // falling back to the raw value where CSS.escape is unavailable.
    _cssEscape(s: string): string {
      return typeof window.CSS?.escape === "function" ? CSS.escape(s) : s;
    },

    // _refreshLeftPane re-fetches + swaps the editor's left pane (the field
    // form) from the server so its inputs, revert buttons, AI badges and
    // repeater aiCells all re-seed from the CURRENT draft. Used after any
    // server-side change the client did NOT apply field-by-field on-screen
    // (discard, version restore, review-list revert / "Revert all") — those
    // otherwise leave stale values + provenance icons until a full reload.
    _refreshLeftPane() {
      const htmx = (window as any).htmx;
      if (!htmx) return;
      const url = window.location.pathname + window.location.search;
      htmx.ajax("GET", url, {
        target: "#cms-editor-leftpane",
        select: "#cms-editor-leftpane",
        swap: "outerHTML",
      });
    },

    // _resetEditState cancels every debounce timer and drops all pending edits +
    // captions. Shared by discard() and onVersionRestored() — both abandon local
    // draft state, so a caption/edit queued within its debounce window must not
    // re-PUT itself after the draft is deleted/restored, resurrecting it. Distinct
    // from flushPending(), which intentionally clears only the save+cap subset and
    // must NOT cancel the preview/recolor/dark timers.
    _resetEditState() {
      if (this._saveTimer) clearTimeout(this._saveTimer);
      if (this._previewTimer) clearTimeout(this._previewTimer);
      if (this._capTimer) clearTimeout(this._capTimer);
      if (this._recolorTimer) clearTimeout(this._recolorTimer);
      if (this._darkReloadTimer) clearTimeout(this._darkReloadTimer);
      this._saveTimer = null;
      this._previewTimer = null;
      this._capTimer = null;
      this._recolorTimer = null;
      this._darkReloadTimer = null;
      this._pending = {};
      this._pendingCaptions = {};
    },

    // flushAndReviewPublish saves any in-flight edits, then opens the shared
    // pre-publish diff drawer (which lazy-loads the diff reflecting the just-saved
    // overlays). The actual publish happens when the operator confirms IN the
    // drawer (its confirm button POSTs /api/publish); this method only prepares.
    async flushAndReviewPublish() {
      const ok = await this.flushPending();
      // A placeholder-invalid field is never sent (see _flushOnce) — name the
      // real reason and take the operator to the field instead of the generic
      // "save failed".
      if (this._blockOnInvalidPlaceholders()) return;
      if (!ok || this._hasPending()) {
        dispatchToast(this._msgs.saveFailed, "error");
        return;
      }
      window.dispatchEvent(new CustomEvent("open-prepublish-diff"));
    },

    async discard() {
      this._resetEditState();
      try {
        const resp = await fetch(`${config.baseURL}/api/draft`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!resp.ok) throw new Error(`discard ${resp.status}`);
        this._setDirty(false);
        this._refreshPublishState();
        this._refreshLeftPane();
        this._refreshPreview();
        dispatchToast(this._msgs.discarded, "success");
      } catch (err) {
        console.error("cms discard failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // onVersionRestored runs after a successful restore POST. The server has
    // committed the restored content, discarded drafts, and enqueued the
    // rebuild; bring every piece of client UI into agreement: exit version
    // preview, clear local draft/dirty state, re-render the form + iframe from
    // the restored published state, and refresh the panel + drive the chip.
    onVersionRestored() {
      const vp = (this as any).$store?.cmsVersionPreview;
      if (vp) {
        vp.active = false;
        vp.pubId = "";
        vp.date = "";
      }
      this._resetEditState();
      this._setDirty(false);
      this._refreshLeftPane();
      this._refreshPreview();
      window.dispatchEvent(new CustomEvent("cms-build-started"));
      // NOTE: do NOT dispatch cms-versions-changed here — the restore handler
      // already returns HX-Trigger: cms-versions-changed (handler_cms_versions.go),
      // which refreshes the panel. Dispatching it again would double-fetch.
    },

    // _onVariantChange handles the per-section Layout dropdown: write the new
    // variant to the draft (context/section/slug/locale come from the URL — the
    // rail keeps it current, same source discard() reads), then re-render the
    // section form so the newly-gated fields appear, then refresh the preview.
    // The write MUST precede the re-GET — the form's field gating reads the new
    // variant. Mirrors discard()'s htmx.ajax GET, but selects only the form.
    async _onVariantChange(select: HTMLSelectElement) {
      // Persist any pending edit BEFORE the layout write: the re-GET below
      // replaces the whole form from the server, and a pending edit still in
      // the browser would be lost. A failed flush already toasted the reason
      // and re-queued the edit, so stop here and show the layout the server
      // still has (the one rendered as selected) instead of an unsaved choice.
      if (!(await this.flushPending())) {
        // A placeholder-held edit saves nothing and toasts nothing on its own:
        // name the field and focus it, as publish does.
        this._blockOnInvalidPlaceholders();
        restoreServerLayout(select);
        return;
      }
      // Entry URLs (e.g. the management-page "Edit content" link) can omit
      // locale/section — a URL-only read made resolveVariantWrite 400 on the
      // FIRST variant switch (blank locale) until a rail click added them. Fill
      // the required params from the authoritative config instead. See
      // buildVariantParams.
      const params = buildVariantParams(window.location.search, config, this.section);
      // Build the re-GET URL BEFORE appending `variant` — the GET re-renders the
      // section form and must not carry the write-only `variant` param.
      const reGetUrl = `${window.location.pathname}?${params.toString()}`;
      params.set("variant", select.value);
      try {
        const resp = await fetch(`${config.baseURL}/api/draft/variant?${params.toString()}`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!resp.ok) {
          // The switch did not save: show the layout the server still has, and
          // surface the failure (same reset as a failed flush above).
          restoreServerLayout(select);
          dispatchToast(this._msgs.saveFailed, "error");
          return;
        }
        // A raw fetch() never auto-processes HX-Trigger — re-dispatch it on
        // document.body so listeners like the management publish button's
        // self-refresh wrapper (hx-trigger="...cms-variant-changed from:body")
        // see this SetVariant write (handler_website_content_draft.go's
        // HXEventCMSVariantChanged).
        processResponseTriggers(resp);
        // A layout-only switch is a publishable change — flip the dirty flag so
        // Publish/Discard activate and the header status pill updates.
        this._setDirty(true);
        const htmx = (window as any).htmx;
        if (htmx) {
          await htmx.ajax("GET", reGetUrl, {
            target: "#cms-editor-form",
            select: "#cms-editor-form",
            swap: "outerHTML",
          });
        }
        this._refreshPreview();
      } catch (err) {
        console.error("cms variant change failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // setPreviewMode is the mode-switcher's @click entry point
    // (cmsPreviewToggleExpr, T1 mobile fix #5 / #5 on the mobile-fixes
    // batch). It used to fire a full-body refresh navigation just to
    // flip the ?preview= param — which (a) tripped the tagName==="BODY"
    // $store.cmsMobileTab reset below (yanking the operator back to the Edit
    // tab on mobile) and (b) could swap a live <body> that now contains the
    // teleported version-history drawer (absent from the server HTML),
    // throwing a null-deref htmx:swapError. Root fix: update the preview mode
    // IN PLACE, with NO body swap anywhere in this path:
    //   1. previewMode flips immediately (reactive, drives both switcher
    //      copies' :class/:aria-pressed — instant feedback, no fetch needed
    //      for the buttons themselves).
    //   2. #cms-editor-main's data-cms-preview-mode dataset attribute is set
    //      to the resolved "full-page"/"section" value _previewMode() reads
    //      elsewhere — a direct 1:1 literal mapping of the mode arg, mirroring
    //      the handler's cmsPreviewMode() (no routing-logic duplication).
    //   3. #cms-editor-leftpane is re-fetched via the SAME targeted, non-body
    //      swap _refreshLeftPane()/every rail-nav click already uses (proven
    //      safe). Every rail link's hx-get + data-cms-preview-url/-update
    //      depend on the mode, so this keeps the NEXT section click on the
    //      mode just picked instead of silently reverting to the stale
    //      server-baked one.
    //   4. Once settled, data-cms-active-preview-url/-update (cms_editor.templ,
    //      emitted on #cms-editor-leftpane for data.Selected under the mode
    //      this request resolved) are read off the freshly rendered pane and
    //      pushed into $store.cmsPreviewURL / _currentPreviewUpdate. The
    //      iframe's :src is bound to that store REACTIVELY (cmsDeviceFrame),
    //      so it updates with ZERO html swap of its own subtree — preserving
    //      the "persistent, never-swapped by rail nav" invariant documented
    //      on #cms-editor-preview-wrap. That pane has no other mode-dependent
    //      server-rendered content, so it never needs to be a swap target.
    //   5. history.pushState (no navigation) keeps the URL's preview=
    //      accurate, same as the old expression did.
    //
    // Race/failure guard (fix-loop round 2): a rapid Full→Section→Full click
    // sequence fires overlapping htmx.ajax requests at the same leftpane
    // target with no cancellation — without a guard, whichever settles LAST
    // wins the DOM/store/URL regardless of which button the operator is
    // actually looking at. _previewModeSeq is a monotonic per-instance token:
    // bumped at entry, captured locally, and re-checked after every await —
    // a call whose token no longer matches `this._previewModeSeq` has been
    // superseded by a later call and bails BEFORE applying any post-await
    // effect (never partially applies a stale response over a newer one). A
    // rejected htmx.ajax (network error) restores the optimistic
    // previewMode/dataset write to the pre-call value — so the button
    // reflects the mode that's ACTUALLY rendered (the leftpane/iframe/rail
    // links never changed) — and returns without throwing, rather than
    // leaving state ahead of the DOM on an unhandled rejection.
    async setPreviewMode(mode: "full" | "section") {
      if (this.previewMode === mode) return;
      const prevMode = this.previewMode;
      const seq = ++this._previewModeSeq;
      this.previewMode = mode;
      const main = this._main();
      if (main) main.dataset.cmsPreviewMode = mode === "full" ? "full-page" : "section";
      await this.flushPending();
      if (seq !== this._previewModeSeq) return; // superseded while flushing
      const htmx = (window as any).htmx;
      const base =
        `${config.baseURL}/edit?context=${encodeURIComponent(config.context)}` +
        `&slug=${encodeURIComponent(config.slug)}&locale=${encodeURIComponent(config.locale)}` +
        `&section=${encodeURIComponent(this.section)}`;
      const url = `${base}&preview=${mode}`;
      if (htmx) {
        try {
          await htmx.ajax("GET", url, {
            target: "#cms-editor-leftpane",
            select: "#cms-editor-leftpane",
            swap: "outerHTML",
          });
        } catch (err) {
          console.error("cms preview-mode swap failed", err);
          // Only restore if THIS call is still the latest one — a later call
          // may already have applied its own (successful) mode by now, and
          // stomping that with this stale failure would be the exact bug
          // this guard exists to prevent.
          if (seq === this._previewModeSeq) {
            this.previewMode = prevMode;
            if (main) main.dataset.cmsPreviewMode = prevMode === "full" ? "full-page" : "section";
          }
          return;
        }
      }
      if (seq !== this._previewModeSeq) return; // superseded while the swap was in flight
      const leftpane = document.getElementById("cms-editor-leftpane");
      const newPreviewURL = leftpane?.dataset.cmsActivePreviewUrl;
      const newPreviewUpdate = leftpane?.dataset.cmsActivePreviewUpdate;
      if (newPreviewUpdate) this._currentPreviewUpdate = newPreviewUpdate;
      if (newPreviewURL) {
        const urlStore = (this as any).$store?.cmsPreviewURL;
        // Booking preview: keep the operator's chosen accommodation.
        const carried = carryBookingPreviewProperty((urlStore?.value as string) || "", newPreviewURL);
        if (urlStore) urlStore.value = carried;
        else {
          const f = this._iframe();
          if (f) f.src = carried;
        }
      }
      history.pushState({ cmsNav: true }, "", url);
    },
  };

  // Alpine binds `this` to the fully-merged object at runtime, so each concern
  // is a partial of `this.`-referencing methods spread together here; cross-
  // concern calls resolve against the merged object, not closures. `config` is
  // threaded into the mixins that need it (autosave content paths, ai-review /
  // history API calls, and preview's _currentPreviewUpdate seed).
  return {
    ...editorAutosave(config),
    ...editorPreview(config),
    ...editorAiReview(config),
    ...editorHistory(config),
    ...core,
  } as CmsEditor;
}
